import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const W = 320;
const H = 64;
const P0 = [10, 50];
const P1 = [150, -6];
const P2 = [284, 50];
const HS = 1.3;
const HX = P2[0] - 3 * HS;
const HY = P2[1] - 14.5 * HS;
const HOME = [HX + 12 * HS, HY + 17.5 * HS];
const ENTRY = (Math.atan2(HOME[1] - P2[1], HOME[0] - P2[0]) * 180) / Math.PI;

const at = (t) => {
  const u = 1 - t;
  return [u * u * P0[0] + 2 * u * t * P1[0] + t * t * P2[0], u * u * P0[1] + 2 * u * t * P1[1] + t * t * P2[1]];
};
const heading = (t) => {
  const dx = 2 * (1 - t) * (P1[0] - P0[0]) + 2 * t * (P2[0] - P1[0]);
  const dy = 2 * (1 - t) * (P1[1] - P0[1]) + 2 * t * (P2[1] - P1[1]);
  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

const SAMPLES = 96;
const LENGTHS = (() => {
  const out = [0];
  let prev = at(0);
  for (let i = 1; i <= SAMPLES; i += 1) {
    const p = at(i / SAMPLES);
    out.push(out[i - 1] + Math.hypot(p[0] - prev[0], p[1] - prev[1]));
    prev = p;
  }
  return out;
})();
const TOTAL = LENGTHS[SAMPLES];
/** Bezier parameter at a fraction of the route's length, so equal fractions are equal distances along the arc. */
const tAt = (f) => {
  const L = Math.max(0, Math.min(1, f)) * TOTAL;
  let i = 0;
  while (i < SAMPLES - 1 && LENGTHS[i + 1] < L) i += 1;
  return (i + (L - LENGTHS[i]) / (LENGTHS[i + 1] - LENGTHS[i] || 1)) / SAMPLES;
};
const D = `M ${P0[0]} ${P0[1]} Q ${P1[0]} ${P1[1]} ${P2[0]} ${P2[1]}`;
const PLANE = 1.35;
/** The nose, relative to the middle of the plane's body (which sits on the route). */
const NOSE = [13, -5];
const NOSE_LENGTH = Math.hypot(...NOSE) * PLANE;
const NOSE_ANGLE = (Math.atan2(NOSE[1], NOSE[0]) * 180) / Math.PI;
/** The furthest the plane flies before landing, so its nose stops at the house wall. */
const CAP = 1 - NOSE_LENGTH / TOTAL;

/** A point on the route at a fraction of its length, continuing straight on past the house. */
const pointAt = (f) => {
  if (f <= 1) return at(tAt(f));
  const h = (heading(1) * Math.PI) / 180;
  return [P2[0] + Math.cos(h) * (f - 1) * TOTAL, P2[1] + Math.sin(h) * (f - 1) * TOTAL];
};

/** The plane's rotation with its body at fraction `f`, turned so the nose also lies on the route. */
const tilt = (f) => {
  const [x, y] = pointAt(f);
  let g = f + NOSE_LENGTH / TOTAL;
  for (let i = 0; i < 3; i += 1) {
    const [nx, ny] = pointAt(g);
    g = f + ((g - f) * NOSE_LENGTH) / Math.hypot(nx - x, ny - y);
  }
  const [nx, ny] = pointAt(g);
  return (Math.atan2(ny - y, nx - x) * 180) / Math.PI - NOSE_ANGLE;
};

const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Where the plane was last drawn, so the next screen picks the flight up from there. 0-1 is the route, 1-2 is flying into the house. */
let lastFlight = null;

const useFlight = (target) => {
  const [v, setV] = useState(() => lastFlight ?? Math.max(0, Math.min(target, CAP) - 0.15));
  const current = useRef(v);
  useEffect(() => {
    const from = current.current;
    if (Math.abs(target - from) < 1e-4 || reducedMotion()) {
      current.current = target;
      lastFlight = target;
      setV(target);
      return undefined;
    }
    const duration = Math.min(1800, 650 + Math.abs(target - from) * 1100);
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const k = Math.min(1, (now - start) / duration);
      const next = from + (target - from) * easeInOut(k);
      current.current = next;
      lastFlight = next;
      setV(next);
      if (k < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return v;
};

const shade = (pct) => `color-mix(in srgb, hsl(var(--brand)) ${pct}%, black)`;

/** Forget where the plane was, so the next FlightPath starts fresh instead of flying back from an earlier screen (e.g. the landing page demo). */
export const resetFlight = () => {
  lastFlight = null;
};

/** Angle of the nose from the middle of the body; rotate a plane by minus this to point its nose along a heading. */
export const PLANE_NOSE_ANGLE = NOSE_ANGLE;

/** The paper plane from the Landin icon: top wing, the lighter body below the fold, and the small keel.
 *  Nose points along +x; 0,0 is the middle of the body, so the plane sits centred on the route and covers a checkpoint. */
export const PaperPlane = () => (
  <g transform={`translate(${NOSE[0]} ${NOSE[1]})`} strokeWidth="1.2" strokeLinejoin="round">
    <path d="M0 0 L-17.1 6.7 L-11.6 12.4 Z" fill="hsl(var(--brand))" stroke="hsl(var(--brand))" />
    <path d="M-19.8 4.1 L-19.5 9.7 L-16.9 7.8 Z" fill={shade(55)} stroke={shade(55)} />
    <path d="M0 0 L-24 0 L-19.8 4.1 L-17.1 6.7 Z" fill={shade(78)} stroke={shade(78)} />
  </g>
);

/** The new home where the route ends: a house outline with the Landin icon's rounded door. The route meets its left wall. */
const Home = ({ landed }) => (
  <svg x={HX} y={HY} width={24 * HS} height={24 * HS} viewBox="0 0 24 24" overflow="visible" data-testid="flight-path-home"
    className={landed ? "text-foreground" : "text-muted-foreground"} fill="none" stroke="currentColor" strokeLinejoin="round">
    <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeWidth="1.75" />
    <path d="M9.5 21v-5.5a2.5 2.5 0 0 1 5 0V21" strokeWidth="1.5" />
  </svg>
);

/** The applicant's route: a paper plane flying from today to the new home. `t` is progress (0-1), `stops` are waypoints (0-1).
 *  With `landed` it flies into the house. */
export const FlightPath = ({ t = 0, stops = [], landed = false, className, testid = "flight-path" }) => {
  const v = useFlight(landed ? 2 : Math.max(0, Math.min(CAP, t)));
  const p = Math.min(1, v);
  const u = Math.max(0, v - 1);
  const [x, y] = u ? [P2[0] + (HOME[0] - P2[0]) * u, P2[1] + (HOME[1] - P2[1]) * u] : at(tAt(p));
  const angle = tilt(p) * (1 - u) + (ENTRY - NOSE_ANGLE) * u;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={cn("block h-auto w-full overflow-visible", className)} role="img"
      aria-label={landed ? "Arrived at your new home" : `${Math.round(p * 100)}% of the way to your new home`}
      data-testid={testid} data-progress={(landed ? 1 : p).toFixed(2)}>
      <path d={D} fill="none" stroke="hsl(var(--muted-foreground) / 0.35)" strokeWidth="1.5" strokeDasharray="1 5" strokeLinecap="round" />
      <path d={D} fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.75" strokeLinecap="round"
        strokeDasharray={TOTAL} strokeDashoffset={TOTAL * (1 - p)} />
      <circle cx={P0[0]} cy={P0[1]} r="3.5" fill="hsl(var(--foreground))" />
      {stops.filter((s) => s > 0 && s < 1).map((s) => {
        const [sx, sy] = at(tAt(s));
        return (
          <circle key={s} cx={sx} cy={sy} r="3.5" strokeWidth="1.5" stroke="hsl(var(--foreground))"
            fill={s <= p + 1e-6 ? "hsl(var(--foreground))" : "hsl(var(--panel))"} style={{ transition: "fill 300ms" }} />
        );
      })}
      {u < 1 && (
        <g transform={`translate(${x} ${y}) rotate(${angle}) scale(${PLANE * (1 - u)})`} opacity={1 - u * u}>
          <PaperPlane />
        </g>
      )}
      <Home landed={landed} />
    </svg>
  );
};

const sections = (steps) =>
  steps.reduce((out, s, i) => (i > 0 && s.section === steps[i - 1].section ? (out[out.length - 1].push(i), out) : [...out, [i]]), []);

/** One checkpoint per section, evenly spaced along the route. */
export const sectionStops = (steps) => {
  const n = sections(steps).length;
  return Array.from({ length: Math.max(0, n - 1) }, (_, k) => (k + 1) / n);
};

/** Where step `index` sits on the route: its section's checkpoint plus its share of that leg. `phase` (0-1) moves within the step. */
export const routeProgress = (steps, index, phase = 0) => {
  const groups = sections(steps);
  const g = Math.max(0, groups.findIndex((x) => x.includes(index)));
  return (g + (groups[g].indexOf(index) + phase) / groups[g].length) / groups.length;
};
